import React, { useEffect, useRef } from "react";
import Button from "components/button";
import Icon from "components/icon";
import Typography from "components/typography";
import { useAppTranslation } from "locale/useAppTranslation";
import type { VehicleModelV1ImportRowError } from "_services/modules/vehicle-model";

interface VehicleModelImportModalProps {
  open: boolean;
  onClose: () => void;
  onDownloadTemplate: () => void;
  isDownloading: boolean;
  selectedFile: File | null;
  onFileSelect: (file: File | null) => void;
  onUpload: () => void;
  isUploading: boolean;
  importErrors: VehicleModelV1ImportRowError[];
}

const VehicleModelImportModal: React.FC<VehicleModelImportModalProps> = ({
  open,
  onClose,
  onDownloadTemplate,
  isDownloading,
  selectedFile,
  onFileSelect,
  onUpload,
  isUploading,
  importErrors,
}) => {
  const { t } = useAppTranslation("vehicleModel");
  const dialogRef = useRef<HTMLDialogElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }
  }, [open]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) {
      onClose();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    onFileSelect(file);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0] ?? null;
    if (file) onFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-white p-6 shadow-lg backdrop:bg-black/50 w-full max-w-lg"
      onClick={handleBackdropClick}
      onClose={onClose}
      data-testid="modal-import-vehicle-model"
    >
      <div className="flex flex-col gap-5">
        {/* Header */}
        <Typography variant="heading4" as="h2">
          {t("import-modal-title")}
        </Typography>

        {/* Step 1: Download Template */}
        <div className="flex flex-col gap-2 rounded-xl border border-border bg-greyScale-98 p-4">
          <Typography variant="body" as="p" className="font-semibold">
            {t("import-step1-title")}
          </Typography>
          <Typography variant="bodySmall" tone="muted" as="p">
            {t("import-step1-desc")}
          </Typography>
          <div className="mt-1">
            <Button
              type="button"
              variant="outline"
              color="primary"
              size="small"
              iconLeft={<Icon name="document-download" size={14} />}
              onClick={onDownloadTemplate}
              disabled={isDownloading}
              data-testid="btn-download-template-vehicle-model"
            >
              {isDownloading ? "..." : t("import-btn-download-template")}
            </Button>
          </div>
        </div>

        {/* Step 2: Upload File */}
        <div className="flex flex-col gap-2">
          <Typography variant="body" as="p" className="font-semibold">
            {t("import-step2-title")}
          </Typography>

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={handleFileChange}
            data-testid="input-file-vehicle-model"
          />

          {selectedFile ? (
            <div className="flex items-center justify-between rounded-xl border border-primary-200 bg-primary-50 px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <Icon name="document-text" size={16} />
                <Typography variant="bodySmall" as="span" className="truncate">
                  {selectedFile.name}
                </Typography>
              </div>
              <button
                type="button"
                onClick={() => onFileSelect(null)}
                className="ml-3 shrink-0 text-greyScale-40 hover:text-greyScale-20 transition-colors"
                data-testid="btn-remove-file-vehicle-model"
              >
                <Icon name="close-circle" size={18} />
              </button>
            </div>
          ) : (
            <div
              role="button"
              tabIndex={0}
              className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-greyScale-98 px-4 py-8 cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
              data-testid="dropzone-vehicle-model"
            >
              <Icon name="document-upload" size={32} />
              <Typography variant="bodySmall" tone="muted" as="p">
                {t("import-file-hint")}
              </Typography>
              <Typography variant="bodySmall" tone="muted" as="p">
                {t("import-file-formats")}
              </Typography>
            </div>
          )}
        </div>

        {/* Import Errors */}
        {importErrors.length > 0 && (
          <div className="flex flex-col gap-2">
            <Typography variant="body" as="p" className="font-semibold text-error-600">
              {t("import-errors-title")}
            </Typography>
            <div className="max-h-48 overflow-y-auto rounded-xl border border-error-200">
              <table className="w-full text-sm">
                <thead className="bg-error-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-greyScale-20 w-16">
                      {t("import-errors-col-row")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-greyScale-20 w-28">
                      {t("import-errors-col-field")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-greyScale-20">
                      {t("import-errors-col-message")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {importErrors.map((err, idx) => (
                    <tr key={idx} className="border-t border-error-100">
                      <td className="px-3 py-2 text-greyScale-20">{err.row}</td>
                      <td className="px-3 py-2 text-greyScale-20">{err.field}</td>
                      <td className="px-3 py-2 text-greyScale-20">{err.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-1">
          <Button
            type="button"
            variant="outline"
            color="tertiary"
            size="medium"
            onClick={onClose}
            data-testid="modal-import-vehicle-model-cancel"
          >
            {t("import-btn-cancel")}
          </Button>
          <Button
            type="button"
            variant="fill"
            color="primary"
            size="medium"
            onClick={onUpload}
            disabled={!selectedFile || isUploading}
            data-testid="modal-import-vehicle-model-confirm"
          >
            {isUploading ? "..." : t("import-btn-confirm")}
          </Button>
        </div>
      </div>
    </dialog>
  );
};

export default VehicleModelImportModal;
